import { Headphones, SpeakerHigh, Ear, Eye, HandTap, Confetti, BookOpen } from "@phosphor-icons/react";
import { SectionShell } from "../../styleguide/Primitives";
import { Showcase } from "../../library/LibraryPrimitives";
import { StackedBars, Flow } from "../diagrams";

const SPECTRUM = [
  { label: "Y1", values: { visual: 40, audio: 35, interactive: 20, text: 5 } },
  { label: "Y2", values: { visual: 35, audio: 30, interactive: 25, text: 10 } },
  { label: "Y3", values: { visual: 30, audio: 25, interactive: 25, text: 20 } },
  { label: "Y4", values: { visual: 25, audio: 15, interactive: 30, text: 30 } },
  { label: "Y5", values: { visual: 20, audio: 10, interactive: 30, text: 40 } },
  { label: "Y6", values: { visual: 15, audio: 5, interactive: 30, text: 50 } },
];

const SEGMENTS = [
  { key: "visual", label: "Visual", color: "bg-teal" },
  { key: "audio", label: "Audio", color: "bg-blue" },
  { key: "interactive", label: "Interactive", color: "bg-primary" },
  { key: "text", label: "Text", color: "bg-secondary" },
];

const STORY = [
  { icon: Ear, label: "Listen", desc: "Narration sets the scene", tone: "blue" },
  { icon: Eye, label: "Observe", desc: "A scene shows the goal", tone: "teal" },
  { icon: HandTap, label: "Interact", desc: "Tap to help the character", tone: "orange" },
  { icon: Confetti, label: "Celebrate", desc: "The story rewards success", tone: "yellow" },
];

const AUDIO_POINTS = ["Questions", "Instructions", "Hints", "Feedback", "Story narration"];

export function SensorySection() {
  return (
    <SectionShell
      id="sensory"
      eyebrow="Multi-Sensory & Audio"
      eyebrowIcon={Headphones}
      title="Not every child can read yet"
      description="Bloom reaches children through sight, sound and touch — never assuming reading. Support shifts gradually from visual & audio in Year 1 toward text by Year 6, so no one is left behind."
    >
      <div className="space-y-8">
        <Showcase title="The sensory shift, Year 1 → Year 6" description="How the balance of delivery matures with the child" tone="muted">
          <StackedBars data={SPECTRUM} segments={SEGMENTS} />
        </Showcase>

        <div className="grid gap-5 lg:grid-cols-2">
          <Showcase title="Audio support">
            <p className="font-medium leading-relaxed text-muted-foreground">A <strong className="text-foreground">speaker control</strong> sits beside every important block — tap to replay. For Years 1–2 (and early Year 3) audio auto-plays.</p>
            <div className="mt-4 space-y-2">
              {AUDIO_POINTS.map((a) => (
                <div key={a} className="flex items-center gap-3 rounded-2xl bg-muted/40 p-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue/12 text-blue-ink"><SpeakerHigh size={20} weight="fill" /></span>
                  <span className="font-bold">{a}</span>
                </div>
              ))}
            </div>
          </Showcase>

          <Showcase title="Story-based learning" description="Listen → Observe → Interact → Celebrate">
            <Flow steps={STORY} />
            <div className="mt-4">
              <p className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Story shapes</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {["Rescue missions", "Collecting objects", "Adventures", "Friendly characters"].map((s) => (
                  <span key={s} className="inline-flex items-center gap-1.5 rounded-full bg-primary/12 px-3 py-1.5 text-sm font-bold text-primary-strong"><BookOpen size={14} weight="fill" />{s}</span>
                ))}
              </div>
            </div>
          </Showcase>
        </div>

        <p className="rounded-3xl border border-border bg-accent/50 p-5 font-medium text-foreground">
          <strong>Principle:</strong> younger children learn through <strong>Listen → Observe → Interact → Celebrate</strong>. Reading comprehension is never required before a child is ready for it.
        </p>
      </div>
    </SectionShell>
  );
}

export default SensorySection;
